import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {map, switchMap, take} from 'rxjs/operators';
import {CommandService} from '@core/services/command.service';
import {FileManagementService} from '@core/services/file-management.service';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {PlatformCommand} from '@shared/commands/command-base';
import {Api} from '@shared/constants/common-field.constants';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {
  AdminDashboardAttention,
  AdminDashboardAttentionExport,
  AdminDashboardCompanyCommercial,
  AdminDashboardCompanyEngagement,
  AdminDashboardCompanyInsight,
  AdminDashboardCompanyProductUse,
  AdminDashboardCompanyTimeline,
  AdminDashboardCompanyTransactions,
  AdminDashboardFailingTopics,
  AdminDashboardFilterOptions,
  AdminDashboardInitFailures,
  AdminDashboardInsightSettings,
  AdminDashboardInsightSettingsResponse,
  AdminDashboardKpi,
  AdminDashboardLicenceExpiry,
  AdminDashboardNonClientCompanies,
  AdminDashboardQueryParams,
  AdminDashboardTrialLikelihoods,
  AdminDashboardXatBotQuality
} from '@features/admin-panel/models/admin-dashboard.models';
import {AdminDashboardInsightImpact} from '@features/admin-panel/models/admin-dashboard-impact.models';
import {AdminDashboardDirectory, AdminDashboardDirectoryParams} from '@features/admin-panel/models/admin-dashboard-directory.models';

/**
 * The Admin dashboard's only data path. Every dashboard query is an `AdminPanelApi` PlatformQuery
 * carrying the shared parameters (Scope, Period and the filters). Response dictionary keys keep
 * their PascalCase; properties inside the returned objects are camelCase.
 */
@Injectable({providedIn: 'root'})
export class AdminDashboardDataService {

  constructor(private platformQueryService: PlatformQueryService, private commandService: CommandService, private fileManagementService: FileManagementService) {}

  getKpi(queryName: string, params: AdminDashboardQueryParams): Observable<AdminDashboardKpi> {
    return this.getData<AdminDashboardKpi>(queryName, params, 'Kpi');
  }

  /** Every header filter's choices plus FiltersPending, in ONE call; each select reads its own key. */
  getFilterOptions(params: AdminDashboardQueryParams): Observable<AdminDashboardFilterOptions> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardFilterOptionsQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardFilterOptions)
    );
  }

  /** The licence expiry timeline's Buckets plus NotApplicable (set for the NonClients scope), in ONE call. */
  getLicenceExpiry(params: AdminDashboardQueryParams): Observable<AdminDashboardLicenceExpiry> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardLicenceExpiryQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardLicenceExpiry)
    );
  }

  /** The "Not in Client Manager" list's Rows, Total and FiltersPending, in ONE call. */
  getNonClientCompanies(params: AdminDashboardQueryParams): Observable<AdminDashboardNonClientCompanies> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardNonClientCompaniesQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardNonClientCompanies)
    );
  }

  /**
   * The Company 360 directory (ITP-E-304-01, Plan ITP-P-68 D-D3): one page of every company, searched, filtered and
   * sorted on the server. Sends the nine directory fields and no Scope; returns the whole fieldValues (Rows, Total,
   * Page, PageSize, AsOf).
   */
  getCompanies(params: AdminDashboardDirectoryParams): Observable<AdminDashboardDirectory> {
    const query = new PlatformQuery();
    query.api = 'AdminPanelApi';
    query.name = 'AdminDashboardCompaniesQuery';
    query.isCamelCase = true;
    query.fieldValues['Search'] = params.search;
    query.fieldValues['Tier'] = params.tier;
    query.fieldValues['Kind'] = params.kind;
    query.fieldValues['Flagged'] = params.flagged;
    query.fieldValues['Status'] = params.status;
    query.fieldValues['Sort'] = params.sort;
    query.fieldValues['Dir'] = params.dir;
    query.fieldValues['Page'] = params.page;
    query.fieldValues['PageSize'] = params.pageSize;
    return this.platformQueryService.executeQuery(query).pipe(
      map(response => response.fieldValues as AdminDashboardDirectory)
    );
  }

  getAttention(params: AdminDashboardQueryParams): Observable<AdminDashboardAttention> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardAttentionQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardAttention)
    );
  }

  getTrialLikelihoods(params: AdminDashboardQueryParams): Observable<AdminDashboardTrialLikelihoods> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardTrialLikelihoodQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardTrialLikelihoods)
    );
  }

  /** The Product quality chart: unresolved share and negative feedback per bucket, with totals vs the previous period. */
  getXatBotQuality(params: AdminDashboardQueryParams): Observable<AdminDashboardXatBotQuality> {
    return this.getData<AdminDashboardXatBotQuality>('AdminDashboardXatBotQualityQuery', params, 'Quality');
  }

  /** The Top failing topics card: Topics (top 6), Total and Riser, in ONE call. */
  getFailingTopics(params: AdminDashboardQueryParams): Observable<AdminDashboardFailingTopics> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardFailingTopicsQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardFailingTopics)
    );
  }

  /** The login-initialisation failures card: Collected and Steps, in ONE call. */
  getLoginInitFailures(params: AdminDashboardQueryParams): Observable<AdminDashboardInitFailures> {
    return this.platformQueryService.executeQuery(this.buildQuery('AdminDashboardLoginInitFailuresQuery', params)).pipe(
      map(response => response.fieldValues as AdminDashboardInitFailures)
    );
  }

  getCompanyInsight(companyId: string): Observable<AdminDashboardCompanyInsight> {
    return this.platformQueryService.executeQuery(this.buildCompanyQuery('AdminDashboardCompanyInsightQuery', companyId)).pipe(
      map(response => response.fieldValues as AdminDashboardCompanyInsight)
    );
  }

  getCompanyTimeline(companyId: string): Observable<AdminDashboardCompanyTimeline> {
    return this.platformQueryService.executeQuery(this.buildCompanyQuery('AdminDashboardCompanyTimelineQuery', companyId)).pipe(
      map(response => response.fieldValues as AdminDashboardCompanyTimeline)
    );
  }

  getCompanyEngagement(companyId: string): Observable<AdminDashboardCompanyEngagement> {
    return this.platformQueryService.executeQuery(this.buildCompanyQuery('AdminDashboardCompanyEngagementQuery', companyId)).pipe(
      map(response => response.fieldValues as AdminDashboardCompanyEngagement)
    );
  }

  getCompanyCommercial(companyId: string): Observable<AdminDashboardCompanyCommercial> {
    return this.platformQueryService.executeQuery(this.buildCompanyQuery('AdminDashboardCompanyCommercialQuery', companyId)).pipe(
      map(response => response.fieldValues as AdminDashboardCompanyCommercial)
    );
  }

  getCompanyTransactions(companyId: string): Observable<AdminDashboardCompanyTransactions> {
    return this.platformQueryService.executeQuery(this.buildCompanyQuery('AdminDashboardCompanyTransactionsQuery', companyId)).pipe(
      map(response => response.fieldValues as AdminDashboardCompanyTransactions)
    );
  }

  getCompanyProductUse(companyId: string): Observable<AdminDashboardCompanyProductUse> {
    return this.platformQueryService.executeQuery(this.buildCompanyQuery('AdminDashboardCompanyProductUseQuery', companyId)).pipe(
      map(response => response.fieldValues as AdminDashboardCompanyProductUse)
    );
  }

  /** The insight settings, their defaults and CanEdit, in ONE call (ITP-E-142-03). */
  getInsightSettings(): Observable<AdminDashboardInsightSettingsResponse> {
    const query = new PlatformQuery();
    query.api = 'AdminPanelApi';
    query.name = 'AdminDashboardInsightSettingsQuery';
    query.isCamelCase = true;
    return this.platformQueryService.executeQuery(query).pipe(
      map(response => response.fieldValues as AdminDashboardInsightSettingsResponse)
    );
  }

  /** Snooze (30 days) or mark handled, with an optional note; or reopen a snoozed / handled flag, with no note (ITP-E-164-02). */
  flagAction(companyId: string, kind: string, action: 'snooze' | 'handled' | 'reopen', note: string | null): Observable<any> {
    const command = new PlatformCommand('AdminDashboardFlagActionCommand', Api.AdminPanel);
    command.fieldValues['CompanyId'] = companyId;
    command.fieldValues['Kind'] = kind;
    command.fieldValues['Action'] = action;
    command.fieldValues['Note'] = note;
    return this.commandService.executePlatformCommand(command);
  }

  /**
   * Builds the Needs attention workbook on the server (every flagged company in scope, snoozed and handled ones
   * included) and downloads it. Errors with the server's ErrorMessage when it sends one.
   */
  exportAttention(params: AdminDashboardQueryParams): Observable<AdminDashboardAttentionExport> {
    const command = new PlatformCommand('ExportAdminDashboardAttentionCommand', Api.AdminPanel);
    Object.assign(command.fieldValues, this.buildQuery('ExportAdminDashboardAttentionCommand', params).fieldValues);
    return this.commandService.executePlatformCommand(command).pipe(
      take(1),
      switchMap(response => {
        const message = response?.fieldValues?.['ErrorMessage'];
        if (message) {
          return throwError(() => new Error(message));
        }
        const model = response?.fieldValues?.['DownloadFileResponseModel'];
        const fileId = model?.fileId ?? model?.FileId;
        if (!fileId) {
          return throwError(() => new Error('The export file was not created.'));
        }
        const fileName: string = model.fileName ?? model.FileName ?? 'needs-attention.xlsx';
        const rows: number = response.fieldValues['Rows'] ?? 0;
        return this.fileManagementService.downloadClientFileWithId(fileId).pipe(
          map(blob => {
            this.fileManagementService.openSaveFileWindow(blob, fileName);
            return {fileName, rows};
          })
        );
      })
    );
  }

  /** Saves the fourteen thresholds and weights (percent values as ratios), each as its own field (ITP-E-142-04, usage ITP-E-216-05). */
  saveInsightSettings(values: AdminDashboardInsightSettings): Observable<any> {
    const command = new PlatformCommand('SaveAdminDashboardInsightSettingsCommand', Api.AdminPanel);
    command.fieldValues['RenewalWindowDays'] = values.renewalWindowDays;
    command.fieldValues['RenewalLoginRatio'] = values.renewalLoginRatio;
    command.fieldValues['LowSeatUse'] = values.lowSeatUse;
    command.fieldValues['HighUnresolved'] = values.highUnresolved;
    command.fieldValues['DarkRatio'] = values.darkRatio;
    command.fieldValues['DarkMinWeekly'] = values.darkMinWeekly;
    command.fieldValues['SpikeMultiplier'] = values.spikeMultiplier;
    command.fieldValues['SpikeMinUsd'] = values.spikeMinUsd;
    command.fieldValues['UsageDropRatio'] = values.usageDropRatio;
    command.fieldValues['UsageMinProjects'] = values.usageMinProjects;
    command.fieldValues['WeightEngagement'] = values.weightEngagement;
    command.fieldValues['WeightSeats'] = values.weightSeats;
    command.fieldValues['WeightRenewal'] = values.weightRenewal;
    command.fieldValues['WeightQuality'] = values.weightQuality;
    return this.commandService.executePlatformCommand(command);
  }

  /** Re-runs the latest night's insights with the saved settings (ITP-E-142-06). */
  recomputeInsights(): Observable<any> {
    return this.commandService.executePlatformCommand(new PlatformCommand('RecomputeAdminDashboardInsightsCommand', Api.AdminPanel));
  }

  /**
   * ITP-E-168-04. The prediction settings impact preview: the latest insight day re-scored under the saved settings
   * (Before) and under `values` (After: the fourteen values on screen, percent fields as ratios), over the companies of
   * `params`. The fourteen fields carry the names `saveInsightSettings` sends. Returns the whole response.
   */
  previewInsightImpact(values: AdminDashboardInsightSettings, params: AdminDashboardQueryParams): Observable<AdminDashboardInsightImpact> {
    const query = this.buildQuery('AdminDashboardInsightImpactQuery', params);
    query.fieldValues['RenewalWindowDays'] = values.renewalWindowDays;
    query.fieldValues['RenewalLoginRatio'] = values.renewalLoginRatio;
    query.fieldValues['LowSeatUse'] = values.lowSeatUse;
    query.fieldValues['HighUnresolved'] = values.highUnresolved;
    query.fieldValues['DarkRatio'] = values.darkRatio;
    query.fieldValues['DarkMinWeekly'] = values.darkMinWeekly;
    query.fieldValues['SpikeMultiplier'] = values.spikeMultiplier;
    query.fieldValues['SpikeMinUsd'] = values.spikeMinUsd;
    query.fieldValues['UsageDropRatio'] = values.usageDropRatio;
    query.fieldValues['UsageMinProjects'] = values.usageMinProjects;
    query.fieldValues['WeightEngagement'] = values.weightEngagement;
    query.fieldValues['WeightSeats'] = values.weightSeats;
    query.fieldValues['WeightRenewal'] = values.weightRenewal;
    query.fieldValues['WeightQuality'] = values.weightQuality;
    return this.platformQueryService.executeQuery(query).pipe(
      map(response => response.fieldValues as AdminDashboardInsightImpact)
    );
  }

  getData<T>(queryName: string, params: AdminDashboardQueryParams, key: string): Observable<T> {
    return this.platformQueryService.executeQuery(this.buildQuery(queryName, params)).pipe(
      map(response => response.fieldValues[key] as T)
    );
  }

  /** Runs a dashboard query with the shared params plus query-specific fields and returns the whole response (ITP-E-159). */
  getWithExtra<T>(queryName: string, params: AdminDashboardQueryParams, extra: Record<string, string | number | boolean | null>): Observable<T> {
    const query = this.buildQuery(queryName, params);
    Object.keys(extra).forEach(key => query.fieldValues[key] = extra[key]);
    return this.platformQueryService.executeQuery(query).pipe(map(response => response.fieldValues as T));
  }

  private buildQuery(queryName: string, params: AdminDashboardQueryParams): PlatformQuery {
    const query = new PlatformQuery();
    query.api = 'AdminPanelApi';
    query.name = queryName;
    query.isCamelCase = true;
    query.fieldValues['Scope'] = params.scope;
    if (params.period !== undefined) {
      query.fieldValues['Period'] = params.period;
    }
    if (params.packages !== undefined) {
      query.fieldValues['Packages'] = params.packages;
    }
    if (params.sources !== undefined) {
      query.fieldValues['Sources'] = params.sources;
    }
    if (params.licenceStatuses !== undefined) {
      query.fieldValues['LicenceStatuses'] = params.licenceStatuses;
    }
    if (params.owners !== undefined) {
      query.fieldValues['Owners'] = params.owners;
    }
    return query;
  }

  private buildCompanyQuery(queryName: string, companyId: string): PlatformQuery {
    const query = new PlatformQuery();
    query.api = 'AdminPanelApi';
    query.name = queryName;
    query.isCamelCase = true;
    query.fieldValues['CompanyId'] = companyId;
    return query;
  }
}
